import { useEffect, useState, useCallback, useRef } from "react";

/**
 * Geolocation hook with reverse geocode of the current position.
 * state:
 *   - status: 'idle' | 'loading' | 'granted' | 'denied' | 'error' | 'unsupported'
 *   - coords: {lat, lng, accuracy} | null
 *   - address: string | null
 *   - error: string | null
 */
export default function useGeolocation({ reverseGeocode } = {}) {
  const [status, setStatus] = useState("idle");
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState(null);
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);

  const request = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setStatus("unsupported");
      setError("La géolocalisation n'est pas supportée par ce navigateur.");
      return;
    }
    setStatus("loading");
    setError(null);
    cancelRef.current = false;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (cancelRef.current) return;
        const c = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        };
        setCoords(c);
        setStatus("granted");

        if (reverseGeocode) {
          try {
            const addr = await reverseGeocode(c.lat, c.lng);
            if (!cancelRef.current) setAddress(addr || null);
          } catch (_e) {
            /* non-blocking */
          }
        }
      },
      (err) => {
        if (cancelRef.current) return;
        if (err.code === err.PERMISSION_DENIED) {
          setStatus("denied");
          setError("Autorisation refusée. Activez la géolocalisation dans les réglages.");
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setStatus("error");
          setError("Position indisponible. Vérifiez votre signal GPS.");
        } else if (err.code === err.TIMEOUT) {
          setStatus("error");
          setError("Délai dépassé pour obtenir votre position.");
        } else {
          setStatus("error");
          setError(err.message || "Erreur de géolocalisation.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }, [reverseGeocode]);

  useEffect(() => {
    request();
    return () => {
      cancelRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, coords, address, error, refresh: request };
}
